import {
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  Button,
  DataStatus,
  closeCurrentDialog,
  openDialog,
  IconButton,
} from 'platform/components';
import {
  GridItem,
  Heading,
  HStack,
  Right,
  Show,
  Grid,
  VStack,
  Stack,
  Box,
} from 'platform/foundation';

import {useState, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil, has, find} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerResponseBodyV2,
  useGetCustomerV2Query,
  useGetVehicleCustomerQuery,
} from '@dms/api/customer';
import {useGetVehicleMileageQuery} from '@dms/api/sales';
import {
  GetServiceCheckInApiResponse,
  GetServiceHandOverApiResponse,
  PostServiceCheckInApiArg,
  PostServiceHandOverApiArg,
  useGetMetadaServiceCaseQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  CustomerSelectSearch,
  usePhoneNumbers,
  ParkingPlace,
  useEditCustomer,
  CustomerMatchOrCreate,
  getCustomerName,
  getPhoneNumber,
  MileageInput,
} from '@dms/shared';
import {FuelLevelInput} from '@dms/shared-aftersales';

import {parseDate, getApiDateTimeString, suffixTestId, TestIdProps} from 'shared/web';

import {useWorkshopUrl} from '../../hooks/useWorkshopUrl';
import {FormActions} from './components/FormActions';
import {CheckInHandOverFormType} from './types/CheckInHandOverFormType';

interface CheckInHandOverFormProps extends TestIdProps {
  isEditing: boolean;
  onEditDiscard: () => void;
  isCheckin?: boolean;
  isReadOnly?: boolean;
  defaultValues?: GetServiceHandOverApiResponse | GetServiceCheckInApiResponse;
  onSubmit: (
    data: PostServiceCheckInApiArg['body'] | PostServiceHandOverApiArg['body']
  ) => Promise<void>;
}

export function CheckInHandOverForm(props: CheckInHandOverFormProps) {
  // nitfy hack until https://carvago.atlassian.net/browse/T20-66063 is fixed
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  const refreshPhoneInputKey = () => setPhoneInputKey((prev) => prev + 1);
  const formApiRef = useRef<UseFormReturn<CheckInHandOverFormType> | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    props.defaultValues?.driver?.id ?? ''
  );

  const {serviceCaseId} = useWorkshopUrl();

  const {
    data: serviceCase,
    isLoading,
    isError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId}, {skip: isNil(serviceCaseId)});

  const {data: vehicleMilage} = useGetVehicleMileageQuery(
    {
      vehicleId: serviceCase?.vehicleId ?? '',
    },
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const {countriesOptions} = usePhoneNumbers();

  const {data: customerData} = useGetCustomerV2Query(
    {customerId: selectedCustomerId},
    {skip: isNilOrEmpty(selectedCustomerId)}
  );

  const {data: vehicleCustomers} = useGetVehicleCustomerQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const vehicleUser = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_USER',
    vehicleCustomers ?? []
  );

  const fleetManager = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_MANAGER',
    vehicleCustomers ?? []
  );

  const vehicleOperator = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
    vehicleCustomers ?? []
  );

  const fallbackVehicleCustomer = vehicleUser ?? fleetManager ?? vehicleOperator;

  const {data: fallbackCustomerData} = useGetCustomerV2Query(
    {customerId: fallbackVehicleCustomer?.customerId ?? ''},
    {skip: isNilOrEmpty(fallbackVehicleCustomer?.customerId)}
  );

  const defaultValues = {
    ...props.defaultValues,
    mileage: props.defaultValues?.mileage ?? vehicleMilage?.mileage ?? 0,
    isVehicleNotInService: has('isVehicleNotInService', props.defaultValues)
      ? props.defaultValues.isVehicleNotInService
      : false,
    isNonDrivableVehicle: has('isNonDrivableVehicle', props.defaultValues)
      ? props.defaultValues.isNonDrivableVehicle
      : false,
    fuelTank: Math.round((props.defaultValues?.fuelTank ?? 0) * 100),
    driverId: props.defaultValues?.driver?.id ?? '',
    driverFullName: props.defaultValues?.driver?.name ?? '',
    driverEmail: props.defaultValues?.driver?.email ?? '',
    driverIdentityCard: props.defaultValues?.driver?.identityCard ?? '',
    driverPhone: props.defaultValues?.driver?.phoneNumber ?? {},
    technicalInspectionDate: props.defaultValues?.technicalInspection
      ? parseDate(props.defaultValues?.technicalInspection)
      : undefined,
    completedDate: props.defaultValues?.completedDate ?? getApiDateTimeString(new Date()),
    isCompleting: false,
  };

  const handleCustomerCreated = (
    customer: CustomerResponseBodyV2,
    formApi: UseFormReturn<CheckInHandOverFormType>
  ) => {
    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
    const phoneNumber = customerPhoneNumber;
    const name = getCustomerName(customer);

    const formattedPhoneNumber = getPhoneNumber(
      phoneNumber?.prefix ?? '',
      phoneNumber?.number ?? '',
      countriesOptions
    );

    formApi.setValue('driverFullName', name ?? '');
    formApi.setValue('driverEmail', email || '');
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverId', customer.id);
    formApi.setValue('driverIdentityCard', customerInfo.identityCards?.[0]?.cardNumber ?? '');
    setSelectedCustomerId(customer.id);
    refreshPhoneInputKey();
    closeCurrentDialog();
  };

  const handleCreateCustomer = (formApi: UseFormReturn<CheckInHandOverFormType>) => {
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={(customer) => handleCustomerCreated(customer, formApi)}
        data-testid={suffixTestId('createCustomer', props)}
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
      }
    );
  };

  const handleSubmit: FormSubmitHandler<CheckInHandOverFormType> = async (data, _, reset) => {
    const {
      technicalInspectionDate,
      driverPhone,
      driverFullName,
      driverEmail,
      driverIdentityCard,
      completedDate,
      isCompleting,
      driverId,
      ...res
    } = data;

    const technicalInspection = technicalInspectionDate
      ? getApiDateTimeString(technicalInspectionDate)
      : null;

    const driver = {
      id: driverId,
      name: driverFullName || null,
      phoneNumber: driverPhone
        ? {
            countryCode: driverPhone.countryCode,
            number: driverPhone.number,
            prefix: driverPhone.prefix,
          }
        : null,
      email: driverEmail || null,
      identityCard: driverIdentityCard || null,
    };

    const payload: PostServiceCheckInApiArg['body'] | PostServiceHandOverApiArg['body'] = {
      ...res,
      fuelTank: Number(data.fuelTank) / 100,
      technicalInspection,
      driver,
      withFuelTankMileageUpdate: data.withFuelTankMileageUpdate || false,
    };

    if (isCompleting && completedDate) {
      payload.completedDate = completedDate;
    }

    await props.onSubmit(payload);
    reset(undefined, {keepValues: true});
  };

  const editCustomer = useEditCustomer(customerData ?? fallbackCustomerData ?? null, (customer) => {
    const formApi = formApiRef.current;
    if (!formApi) {
      return;
    }

    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
    const phoneNumber = customerPhoneNumber;
    const name = getCustomerName(customer);

    const formattedPhoneNumber = getPhoneNumber(
      phoneNumber?.prefix ?? '',
      phoneNumber?.number ?? '',
      countriesOptions
    );

    formApi.setValue('driverFullName', name ?? '');
    formApi.setValue('driverEmail', email || '');
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverId', customer.id);
    formApi.setValue('driverIdentityCard', customerInfo.identityCards?.[0]?.cardNumber ?? '');
    refreshPhoneInputKey();
  });

  const handleEditCustomer = () => {
    editCustomer?.();
  };

  return (
    <Form<CheckInHandOverFormType>
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isDisabled={props.isReadOnly}
    >
      {(control, formApi) => {
        // Store formApi in ref so it can be accessed in the afterEdit callback
        formApiRef.current = formApi;

        return (
          <VStack spacing={4}>
            <FormField name="driverId" type="hidden" control={control} />
            <FormField name="completedDate" type="hidden" control={control} />
            <FormField name="isCompleting" type="hidden" control={control} />
            <Heading size={4}>{i18n.t('entity.workshop.labels.vehicleDriver')}</Heading>
            <CustomerSelectSearch
              onCustomerSelect={(data) => {
                const formattedPhoneNumber = getPhoneNumber(
                  data.prefix,
                  data.phoneNumber,
                  countriesOptions
                );

                formApi.setValue('driverFullName', data.name);
                formApi.setValue('driverEmail', data.email);
                formApi.setValue('driverPhone', {
                  countryCode: formattedPhoneNumber.countryCode || 'CZE',
                  number: formattedPhoneNumber.number || '',
                  prefix: formattedPhoneNumber.prefix || '',
                });
                formApi.setValue('driverId', data.customerId);
                setSelectedCustomerId(data.customerId);
              }}
              isDisabled={props.isReadOnly}
              onAfterCustomerSelect={refreshPhoneInputKey}
            />
            <Stack direction={['column', 'column', 'column', 'row']} spacing={4} align="flex-end">
              <Grid columns={[2, 2, 2, 4]} grow={1} spacing={4} align="flex-end">
                <GridItem>
                  <FormField
                    label={i18n.t('general.labels.nameAndSurname')}
                    name="driverFullName"
                    type="text"
                    isDisabled
                    control={control}
                    data-testid={suffixTestId('driver', props)}
                  />
                </GridItem>
                <GridItem>
                  <FormField
                    key={phoneInputKey}
                    label={i18n.t('entity.phoneNumber.labels.number')}
                    name="driverPhone"
                    type="phone"
                    isDisabled
                    control={control}
                    countries={countriesOptions}
                    data-testid={suffixTestId('driver', props)}
                  />
                </GridItem>
                <GridItem>
                  <FormField
                    label={i18n.t('general.labels.emailAddress')}
                    name="driverEmail"
                    type="email"
                    isDisabled
                    control={control}
                    data-testid={suffixTestId('email', props)}
                  />
                </GridItem>
                <GridItem>
                  <HStack spacing={4} align="flex-end">
                    <Box width="100%">
                      <FormField
                        label={i18n.t('general.labels.identityCard')}
                        name="driverIdentityCard"
                        type="text"
                        isDisabled
                        control={control}
                        data-testid={suffixTestId('identityCard', props)}
                      />
                    </Box>

                    <HStack spacing={1}>
                      <IconButton
                        onClick={handleEditCustomer}
                        priority="secondary"
                        icon="image/edit"
                        isDisabled={props.isReadOnly}
                      />
                      <IconButton
                        onClick={() => handleCreateCustomer(formApi)}
                        priority="secondary"
                        icon="content/add_circle"
                        isDisabled={props.isReadOnly}
                      />
                    </HStack>
                  </HStack>
                </GridItem>
              </Grid>
            </Stack>
            <Separator spacing={0} />
            <Heading size={4}>{i18n.t('entity.workshop.labels.vehicleInformation')}</Heading>
            <Grid columns={[2, 2, 2, 4]} spacing={4}>
              <GridItem>
                <MileageInput
                  control={control}
                  formApi={formApi}
                  label={i18n.t('entity.vehicle.labels.actualMileage')}
                  name="mileage"
                  vehicleId={serviceCase?.vehicleId ?? ''}
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('mileage', props)}
                />
              </GridItem>
              <GridItem>
                <FormField
                  label={i18n.t('entity.workshop.labels.technicalInspectionValidUntil')}
                  name="technicalInspectionDate"
                  type="date"
                  control={control}
                  isRelativeDatesHidden
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('scheduledDate', props)}
                />
              </GridItem>
              <GridItem span={2}>
                <FuelLevelInput
                  name="fuelTank"
                  control={control}
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('fuelTank', props)}
                />
              </GridItem>
            </Grid>
            <Show when={props.isCheckin}>
              <Grid columns={[2, 2, 2, 4]} spacing={4} align="flex-end">
                <GridItem>
                  <DataStatus isLoading={isLoading} isError={isError}>
                    <ParkingPlace
                      recordId={serviceCase?.vehicleId ?? ''}
                      withLabel
                      isDisabled={props.isReadOnly}
                      data-testid={suffixTestId('parkingPlace', props)}
                    />
                  </DataStatus>
                </GridItem>
                <GridItem>
                  <FormField
                    name="consentToTestDrive"
                    type="checkbox"
                    control={control}
                    label={i18n.t('entity.order.labels.consentToTestDrive')}
                    isDisabled={props.isReadOnly}
                    data-testid={suffixTestId('consentToTestDrive', props)}
                  />
                </GridItem>
                <GridItem>
                  <FormField
                    name="isVehicleNotInService"
                    type="checkbox"
                    control={control}
                    label={i18n.t('entity.order.labels.vehicleIsNotInService')}
                    isDisabled={props.isReadOnly}
                    data-testid={suffixTestId('vehicleIsNotInService', props)}
                  />
                </GridItem>
                <GridItem>
                  <FormField
                    name="isNonDrivableVehicle"
                    type="checkbox"
                    control={control}
                    label={i18n.t('entity.order.labels.isNonDrivableVehicle')}
                    isDisabled={props.isReadOnly}
                    data-testid={suffixTestId('isNonDrivableVehicle', props)}
                  />
                </GridItem>
              </Grid>
            </Show>
            <Separator spacing={0} />
            <FormField
              label={i18n.t('general.labels.note')}
              name="note"
              type="textarea"
              control={control}
              isDisabled={props.isReadOnly}
              data-testid={suffixTestId('note', props)}
            />
            <Right>
              <HStack spacing={2}>
                <Show when={props.isEditing}>
                  <Button
                    data-testid={suffixTestId('discardEditing', props)}
                    onClick={props.onEditDiscard}
                    variant="secondary"
                    isDisabled={props.isReadOnly}
                    title={i18n.t('general.actions.discard')}
                  />
                </Show>
                <FormActions
                  control={control}
                  formApi={formApi}
                  handleSubmit={handleSubmit}
                  isEditing={props.isEditing}
                  defaultFuelTank={props.defaultValues?.fuelTank}
                  defaultMileage={props.defaultValues?.mileage}
                  data-testid={suffixTestId('actions', props)}
                />
              </HStack>
            </Right>
          </VStack>
        );
      }}
    </Form>
  );
}
